import React, { useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { GoArrowRight } from "react-icons/go";
import dayjs from "dayjs";

// Assets
import IconXSrc from "assets/x-black.svg";
import IInfo from "assets/i-info.js";

// Components
import Modal from "components/organism/modal-v2";
import Button from "components/v1/Button";

// API
import { useExportLeadTrackers } from "api/leads-management/mutation";

// Add style to hide default calendar icon
const dateInputStyle = {
  colorScheme: 'light',
};

const ModalDetailFeed = ({ onClose, ...props }) => {
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  
  const { mutate: downloadReport, isPending } = useExportLeadTrackers();

  const isValidDateRange = () => {
    if (!periodFrom || !periodTo) return false;

    const startDate = dayjs(periodFrom);
    const endDate = dayjs(periodTo);

    if (endDate.isBefore(startDate)) return false;

    const diffMonths = endDate.diff(startDate, "month");
    const sameDay = startDate.date() === endDate.date();
    const oneMoreDay = endDate.date() < startDate.date();

    return diffMonths < 3 || (diffMonths === 3 && (sameDay || oneMoreDay));
  };

  const handleDownload = () => {
    if (!isValidDateRange()) return;

    const formattedStartDate = dayjs(periodFrom).format("DD-MM-YYYY");
    const formattedEndDate = dayjs(periodTo).format("DD-MM-YYYY");

    downloadReport(
      {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      },
      {
        onSuccess: (response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;

          const fileName = `Laporan Leads ${formattedStartDate} - ${formattedEndDate}.xlsx`;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          setPeriodFrom("");
          setPeriodTo("");
          onClose();
        },
        onError: (error) => {
          console.error("Error downloading report:", error);
        },
      }
    );
  };

  const handleResetFilter = () => {
    setPeriodFrom("");
    setPeriodTo("");
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} {...props}>
      <div
        className="w-[426px] z-[42] bg-white rounded-md border-[0.5px] border-solid border-[#ECECEC] 
        shadow-[0px_1px_3px_0px_rgba(16,16,16,0.10),_0px_1px_2px_0px_rgba(16,16,16,0.06)]"
      >
        <div className="py-2 px-4 flex items-center justify-between border-b border-solid border-[#D8D8D9]">
          <span className="text-[17px] font-inter-semibold text-[#252526]">
            Download Laporan Leads
          </span>
          <img
            src={IconXSrc}
            alt="Close"
            onClick={onClose}
            className="cursor-pointer w-[14px] h-[14px] object-contain"
          />
        </div>

        <div className="px-4 py-2">
          <div className="mb-4">
            <label className="block text-[13px] font-inter-medium text-[#252526] mb-2">
              Pilih Periode Laporan
            </label>

            <div className="flex items-center gap-2">
              <div className="relative w-full">
                <input
                  type="date"
                  className="w-full rounded-[4px] border-[1px] border-solid border-[#D8D8D9] p-2 pr-8 cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  style={dateInputStyle}
                  value={periodFrom}
                  onChange={(e) => setPeriodFrom(e.target.value)}
                />
                <MdKeyboardArrowDown className="absolute right-2 top-1/2 -translate-y-1/2 text-[#686A68] text-xl pointer-events-none" />
              </div>
              <div>
                <GoArrowRight className="text-[#686A68] text-xl" />
              </div>
              <div className="relative w-full">
                <input
                  type="date"
                  className="w-full rounded-[4px] border-[1px] border-solid border-[#D8D8D9] p-2 pr-8 cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  style={dateInputStyle}
                  value={periodTo}
                  onChange={(e) => setPeriodTo(e.target.value)}
                />
                <MdKeyboardArrowDown className="absolute right-2 top-1/2 -translate-y-1/2 text-[#686A68] text-xl pointer-events-none" />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <IInfo width="16" height="16" color="#686A68" />
              <p className="text-[13px] font-inter text-[#686A68]">Maksimal periode laporan adalah 3 bulan.</p>
            </div>
          </div>
          <div className="flex gap-2 mt-6 justify-end">
            <Button onClick={handleResetFilter} text="Batal" type="secondary" />
            <Button 
              onClick={handleDownload} 
              text={isPending ? "Downloading..." : "Download"} 
              disabled={!isValidDateRange() || isPending}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default function Wrapper({ isOpen, ...props }) {
  if (!isOpen) return null;
  return <ModalDetailFeed isOpen={isOpen} {...props} />;
}
